import { InvalidLoginException } from "../../exceptions/invalid-login-exception";
import {
    HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse, HttpErrorResponse
} from "@angular/common/http";
import {
    Injectable, inject
} from "@angular/core";
import {
    Observable, throwError, OperatorFunction,
    EMPTY,
    MonoTypeOperatorFunction
} from "rxjs";
import {
    tap, catchError
} from "rxjs/operators";
import { InvalidTokenException } from "../../exceptions/invalid-token-exception";
import { ExpiredTokenException } from "../../exceptions/expired-token-exception";

import { BadRequestException } from "../../exceptions/bad-request-exception";
import { ValidationException } from "../../exceptions/validation-exception";
import { ForbiddenException } from "../../exceptions/forbidden-exception";
import { RecordExistsException } from "../../exceptions/record-exists-exception";
import { RecordModifiedException } from "../../exceptions/record-modified-exception";
import { MissingTokenException } from "../../exceptions/missing-token-exception";
import { NotFoundException } from "../../exceptions/not-found-exception";
import { IInternalApiConfig } from "../interfaces/internal-api-config";
import { HttpClientException } from "../../exceptions/http-client-exception";
import { COMMON_APP_SERVICE } from "../../application/constants/app-tokens";
import { IAppService } from "../../application/interfaces/app-service";

/**
 * Intercepts calls to internal API and appends the Authentication Token if necessary
 *
 * Processes Errors from Internal API
 */
@Injectable()

export class InternalApiInterceptor implements HttpInterceptor {
    private readonly _app = inject(COMMON_APP_SERVICE) as IAppService<IInternalApiConfig>;

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        if (request.url.startsWith(this._app.config.apiUrl)) {

            const headers: Record<string, string> = {};

            // Add the Authorization header, if it's not there already.
            const token = this._app.authService.authToken;
            if (token && !request.headers.has("Authorization")) {
                headers["Authorization"] = token.headerValue;
            }

            request = request.clone({ setHeaders: headers });

            return next.handle(request).pipe(
                this._processResponse(),
                this._processError()
            );
        }

        // Do nothing if it's not a internal api request
        return next.handle(request);
    }

    private _processResponse(): MonoTypeOperatorFunction<HttpEvent<any>> {

        return tap((response: HttpEvent<any>) => {

            if (response instanceof HttpResponse && response?.headers) {
                // firefox uses capitalized headers while chrome not.
                const responseToken = response.headers.get("Authorization") || response.headers.get("authorization");
                if (responseToken) {
                    try {
                        this._app.authService.updateAuthToken(responseToken);
                    }
                    catch (error) {
                        // Internal API should not have returned an invalid token
                        // We will create an InvalidToken exception to ensure that the error is properly logged
                        // This error will be handled in the _processError function
                        throw new InvalidTokenException(`Http Response from ${response.url} had an invalid Authorization header: ${(error as Error).message}`, origin);
                    }
                }
            }
        });
    }

    /**
     * Take the HttpError Response and creates internal-api specific CoreUiException and throws them to be handled by
     * the HttpErrorInterceptor.
     */
    private _processError(): OperatorFunction<HttpEvent<any>, HttpEvent<any>> {

        return catchError((error: unknown) => {

            if (error instanceof HttpErrorResponse) {

                const message: string = error.error && error.error.message ? error.error.message : "An error has occurred";
                const errorType: string | undefined = (error.error as {errorType?: string})?.errorType;
                switch (error.status) {
                    case 400:
                        if (message.match(/validation error:/i)) {
                            error = new ValidationException(message.replace(/validation error:/i, "").trim(), error);
                        }
                        else {
                            error = new BadRequestException(message, error);
                        }
                        break;

                    case 401:
                        if (errorType === "InvalidCredentialsError" || message.match(/failed to login user/i)) {
                            error = new InvalidLoginException();
                            // Propagate through the regular error chain so authentication.service
                            // can show the inline invalid-credentials message. Deliberately does
                            // NOT call handleTokenError, which would redirect to the login page.
                            break;
                        }
                        if (message.match(/expired token/i)) {
                            error = new ExpiredTokenException();
                        }
                        else if (message.match(/missing authentication/i)) {
                            error = new MissingTokenException();
                        }
                        else {
                            // Include the original error for logging.
                            // The CommonError handler will wrap this in an exception with a user friendly message.
                            error = new InvalidTokenException(message, error);
                        }
                        // The error handler will redirect the user to the login page
                        // And display an appropriate error message
                        this._app.errorHandler.handleTokenError(error);

                        // This will complete the observable without emitting a response
                        return EMPTY;
                    case 403:
                        error = new ForbiddenException(message, error);
                        // The error handler will redirect the user to the error page
                        // And display an appropriate error message
                        this._app.errorHandler.handleForbiddenError(error);
                        // This will complete the observable without emitting a response
                        return EMPTY;
                    case 404:
                        error = new NotFoundException(message, error);
                        break;
                    case 409:
                        if (message.match(/already exists/i)) {
                            error = new RecordExistsException(
                                "A record with that id already exists. Please try again with a different id.", error
                            );
                        }
                        else {
                            error = new RecordModifiedException(
                                "Data has been modified since it was loaded in your browser. Please refresh and try again.", error
                            );
                        }
                        break;
                    case 500:
                        error = new HttpClientException(message);
                        break;
                }
            }
            // The error will be handled by the CommonHttpInterceptor from here
            return throwError(() => error);
        });
    }
}
